import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtNum } from "@/lib/format";
import { Plus, Trash2, Package, Zap } from "lucide-react";

export default function BOM() {
  const [boms, setBoms] = useState([]);
  const [items, setItems] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openAsm, setOpenAsm] = useState(null);
  const [form, setForm] = useState({ parent_item_id: "", output_qty: 1, labour_cost_per_unit: 0, overhead_cost_per_unit: 0,
    code: "", version: "v1", effective_from: "", effective_to: "", routing_id: "", is_default: true, bom_type: "manufacturing",
    components: [{ item_id: "", qty: 1, scrap_pct: 0, is_phantom: false, is_alternate: false, alt_group: "" }] });
  const [routings, setRoutings] = useState([]);
  const [explode, setExplode] = useState(null);
  const [asmQty, setAsmQty] = useState(1);

  const load = async () => {
    setBoms((await api.get("/bom")).data);
    setItems((await api.get("/items")).data);
    setAssemblies((await api.get("/bom/assemblies")).data);
    try { setRoutings((await api.get("/mfg/routings")).data); } catch { setRoutings([]); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.parent_item_id) return toast.error("Select parent item");
    const comps = form.components.filter((c) => c.item_id && Number(c.qty) > 0);
    if (comps.length === 0) return toast.error("Add at least one component");
    const payload = { ...form, components: comps,
      effective_from: form.effective_from || null,
      effective_to: form.effective_to || null,
      routing_id: form.routing_id || null };
    try { await api.post("/bom", payload); toast.success("BOM created"); setOpenNew(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doExplode = async (bom) => {
    try { const r = await api.get(`/bom/${bom.id}/explode`, { params: { qty: 1, resolve_phantoms: true } });
          setExplode({ bom, rows: r.data }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const assemble = async (bomId) => {
    try { await api.post(`/bom/${bomId}/assemble`, { qty: asmQty }); toast.success(`Assembled ${asmQty} batches`); setOpenAsm(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="bom-page">
      <PageHeader eyebrow="Manufacturing" title="Bill of Materials" description="Define components required per finished item, then assemble batches to consume raw material and add to finished stock."
        actions={<Button onClick={() => setOpenNew(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="new-bom-btn">+ New BOM</Button>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {boms.length === 0 && <div className="md:col-span-2 card-sharp p-8 text-center text-muted-foreground">No BOMs yet — define one to start assembly.</div>}
        {boms.map((b) => (
          <div key={b.id} className="card-sharp p-5" data-testid={`bom-${b.parent_sku}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-accent">BOM · outputs {b.output_qty}</div>
                <div className="font-heading font-semibold text-lg mt-1 flex items-center gap-2"><Package className="w-4 h-4" /> {b.parent_name}</div>
                <div className="text-xs font-mono text-muted-foreground">{b.parent_sku}</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => doExplode(b)} className="rounded-sm gap-1" data-testid={`explode-${b.parent_sku}`}>Explode</Button>
                <Button size="sm" onClick={() => { setOpenAsm(b); setAsmQty(1); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid={`assemble-${b.parent_sku}`}><Zap className="w-3.5 h-3.5" /> Assemble</Button>
              </div>
            </div>
            <div className="border-t pt-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Components</div>
              <table className="w-full text-sm">
                <tbody>
                  {b.components.map((c) => (
                    <tr key={c.item_id} className="border-b border-border/50">
                      <td className="py-1.5 font-mono text-xs w-24">{c.sku}</td>
                      <td className="py-1.5">{c.name}</td>
                      <td className="py-1.5 text-right tabular-nums">{c.qty}</td>
                      <td className="py-1.5 text-right text-xs text-muted-foreground">stock {fmtNum(c.stock_on_hand)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      <div className="card-sharp overflow-hidden">
        <div className="px-4 py-3 border-b text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Recent assemblies</div>
        <table className="w-full dense">
          <thead><tr><th>Date</th><th>BOM</th><th className="text-right">Batches</th><th className="text-right">Output qty</th></tr></thead>
          <tbody>
            {assemblies.length === 0 && <tr><td colSpan="4" className="text-center py-8 text-muted-foreground">No assemblies yet</td></tr>}
            {assemblies.map((a) => (
              <tr key={a.id}>
                <td className="text-xs">{new Date(a.created_at).toLocaleString("en-IN")}</td>
                <td>{a.parent_name}</td>
                <td className="text-right tabular-nums">{a.qty_batches}</td>
                <td className="text-right tabular-nums">{a.output_qty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={openNew} onOpenChange={setOpenNew}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none overflow-y-auto space-y-3">
          <SheetHeader><SheetTitle className="font-heading">New BOM</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Parent (finished) item</Label>
              <Select value={form.parent_item_id} onValueChange={(v) => setForm({ ...form, parent_item_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="bom-parent"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto">{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</div></SelectContent>
              </Select>
            </div>
            <div><Label>Output qty per batch</Label><Input type="number" value={form.output_qty} onChange={(e) => setForm({ ...form, output_qty: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums" /></div>
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" placeholder="BOM-001" /></div>
            <div><Label>Version</Label><Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} className="rounded-sm mt-1 font-mono" /></div>
            <div><Label>Effective from</Label><Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} className="rounded-sm mt-1" data-testid="bom-eff-from" /></div>
            <div><Label>Effective to</Label><Input type="date" value={form.effective_to} onChange={(e) => setForm({ ...form, effective_to: e.target.value })} className="rounded-sm mt-1" /></div>
            <div className="col-span-2"><Label>Routing (optional)</Label>
              <Select value={form.routing_id} onValueChange={(v) => setForm({ ...form, routing_id: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="(none)" /></SelectTrigger>
                <SelectContent>{routings.map((r) => <SelectItem key={r.id} value={r.id}>{r.code} · {r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="border rounded-sm">
            <div className="px-3 py-2 border-b text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Components — v2 (scrap %, phantom, alternates)</div>
            <table className="w-full dense">
              <thead><tr><th>Item</th><th className="text-right">Qty</th><th className="text-right">Scrap %</th><th className="text-center">Phantom</th><th className="text-center">Alt</th><th>Alt group</th><th></th></tr></thead>
              <tbody>
                {form.components.map((c, i) => (
                  <tr key={i} data-testid={`bom-comp-${i}`}>
                    <td className="p-1">
                      <Select value={c.item_id} onValueChange={(v) => setForm((f) => ({ ...f, components: f.components.map((x, j) => j === i ? { ...x, item_id: v } : x) }))}>
                        <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue placeholder="Component" /></SelectTrigger>
                        <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto">{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}</div></SelectContent>
                      </Select>
                    </td>
                    <td className="p-1"><Input type="number" value={c.qty} onChange={(e) => setForm((f) => ({ ...f, components: f.components.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) } : x) }))} className="rounded-sm h-8 text-right tabular-nums w-20" /></td>
                    <td className="p-1"><Input type="number" step="0.1" value={c.scrap_pct || 0} onChange={(e) => setForm((f) => ({ ...f, components: f.components.map((x, j) => j === i ? { ...x, scrap_pct: Number(e.target.value) } : x) }))} className="rounded-sm h-8 text-right tabular-nums w-20" /></td>
                    <td className="p-1 text-center"><input type="checkbox" checked={!!c.is_phantom} onChange={(e) => setForm((f) => ({ ...f, components: f.components.map((x, j) => j === i ? { ...x, is_phantom: e.target.checked } : x) }))} data-testid={`bom-phantom-${i}`} /></td>
                    <td className="p-1 text-center"><input type="checkbox" checked={!!c.is_alternate} onChange={(e) => setForm((f) => ({ ...f, components: f.components.map((x, j) => j === i ? { ...x, is_alternate: e.target.checked } : x) }))} data-testid={`bom-alt-${i}`} /></td>
                    <td className="p-1"><Input value={c.alt_group || ""} onChange={(e) => setForm((f) => ({ ...f, components: f.components.map((x, j) => j === i ? { ...x, alt_group: e.target.value } : x) }))} className="rounded-sm h-8 text-xs w-24" placeholder="e.g. M8_BOLT" /></td>
                    <td className="text-center">{form.components.length > 1 && <button onClick={() => setForm((f) => ({ ...f, components: f.components.filter((_, j) => j !== i) }))}><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="p-2"><Button variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, components: [...f.components, { item_id: "", qty: 1, scrap_pct: 0, is_phantom: false, is_alternate: false, alt_group: "" }] }))} className="rounded-sm gap-1"><Plus className="w-3.5 h-3.5" /> Component</Button></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Labour cost / unit</Label><Input type="number" value={form.labour_cost_per_unit} onChange={(e) => setForm({ ...form, labour_cost_per_unit: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums" /></div>
            <div><Label>Overhead / unit</Label><Input type="number" value={form.overhead_cost_per_unit} onChange={(e) => setForm({ ...form, overhead_cost_per_unit: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums" /></div>
          </div>
          <Button onClick={create} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="bom-save">Create BOM</Button>
        </SheetContent>
      </Sheet>

      <Sheet open={!!openAsm} onOpenChange={(o) => !o && setOpenAsm(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          {openAsm && (
            <>
              <SheetHeader><SheetTitle className="font-heading">Assemble {openAsm.parent_name}</SheetTitle></SheetHeader>
              <div className="text-sm text-muted-foreground">Each batch consumes:</div>
              <ul className="text-xs space-y-1">
                {openAsm.components.map((c) => (
                  <li key={c.item_id} className="flex justify-between border-b border-border/50 py-1">
                    <span>{c.name}</span>
                    <span className="font-mono">{c.qty * asmQty} <span className="text-muted-foreground">(have {fmtNum(c.stock_on_hand)})</span></span>
                  </li>
                ))}
              </ul>
              <div><Label>Number of batches</Label><Input type="number" min="1" value={asmQty} onChange={(e) => setAsmQty(Number(e.target.value))} className="rounded-sm mt-1 tabular-nums" data-testid="asm-qty" /></div>
              <div className="text-sm">→ produces <strong>{openAsm.output_qty * asmQty}</strong> {openAsm.parent_name}</div>
              <Button onClick={() => assemble(openAsm.id)} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="asm-confirm"><Zap className="w-3.5 h-3.5" /> Post assembly</Button>
            </>
          )}
        </SheetContent>
      </Sheet>
      <Sheet open={!!explode} onOpenChange={(o) => !o && setExplode(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none space-y-3 overflow-y-auto">
          {explode && (
            <>
              <SheetHeader><SheetTitle className="font-heading">Explosion · {explode.bom.parent_name}</SheetTitle></SheetHeader>
              <div className="text-xs text-muted-foreground">Leaf-level requirements (phantoms resolved, scrap applied).</div>
              <div className="card-sharp overflow-hidden">
                <table className="w-full dense">
                  <thead><tr><th>Item</th><th className="text-right">Qty req</th><th className="text-right">Scrap %</th><th className="text-right">On hand</th><th>Op</th><th>Alternates</th></tr></thead>
                  <tbody>
                    {explode.rows.map((r) => (
                      <tr key={r.item_id}>
                        <td>{r.name}<div className="text-[10px] font-mono text-muted-foreground">{r.sku}</div></td>
                        <td className="text-right tabular-nums">{r.qty_required}</td>
                        <td className="text-right tabular-nums text-xs">{r.scrap_pct}</td>
                        <td className={`text-right tabular-nums text-xs ${r.on_hand < r.qty_required ? "text-red-500" : "text-emerald-600"}`}>{r.on_hand}</td>
                        <td className="text-[10px] font-mono">{r.op_seq || "—"}</td>
                        <td>{r.alternates.length === 0 ? <span className="text-muted-foreground text-xs">—</span> : r.alternates.map((a) => <div key={a.item_id} className="text-[10px]">↔ {a.name} ({a.qty_required})</div>)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
